import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  InputAdornment,
} from "@mui/material";
import { Image, Anchor } from "hesus-ui-kit";
import { useDebounce } from "use-debounce";
import searchIcon from "@icons/ic-search-blue.svg";
import { addressSingleLine } from "@utils/utils";
import { autoSearchInputStyles } from "@utils/extra-style";
import {
  Block,
  BlockHeader,
  CrossIcon,
  BlockBody,
  InfoLine,
  InfoLabel,
  WarningText,
  PlaceholderText,
} from "./CompanySite.styled";

const ONGOING_SITE_STATUSES = [3, 12, 13, 14];

const SelectedSite = ({
  t,
  site,
  countryId,
  countryList,
  companyId,
  companySites,
  onSelectSite,
  onRemoveSite,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    // If a company is already selected and has sites, use them directly without an extra API call.
    if (companyId && companySites?.length) {
      const filtered = debouncedSearch
        ? companySites.filter((s) =>
            `${s.id} ${s.name}`
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase())
          )
        : companySites;
      setOptions(filtered);
      return;
    }
    if (!countryId || debouncedSearch.length < 1) {
      setOptions([]);
      return;
    }
    setLoading(true);
    axios
      .get(
        `/clientInvoices/sites?countries=[${countryId}]&isActive=true&statusesId=${JSON.stringify(
          ONGOING_SITE_STATUSES
        )}${companyId ? `&companyId=${companyId}` : ""}&search=${encodeURIComponent(
          debouncedSearch
        )}&limit=-1`
      )
      .then((response) => {
        setOptions(response.data?.sites || []);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [debouncedSearch, countryId, companyId, companySites]);

  const handleFocus = () => {
    if (companyId && companySites?.length) {
      setOptions(companySites);
    }
  };

  if (site?.id) {
    const hasPaymentDelegation = !!site.delegatedCompany?.id;
    return (
      <Block>
        <BlockHeader $isSelected>
          {t("CLIENT_INVOICING_SELECTED_SITE")}
          <CrossIcon onClick={onRemoveSite} aria-hidden="true">
            &times;
          </CrossIcon>
        </BlockHeader>
        <BlockBody>
          <InfoLine>
            <InfoLabel>{t("CLIENT_INVOICE_SITE")} : </InfoLabel>
            <Anchor
              url={`/site/view/${site.id}/general-info`}
              target="_blank"
              rel="noreferrer"
              styles={{ color: "#1bace4", textDecoration: "underline" }}
            >
              {`#${site.id} - ${site.name}`}
            </Anchor>
          </InfoLine>
          {site.address && (
            <InfoLine>
              <InfoLabel>{t("ADDRESS")} : </InfoLabel>
              <span
                dangerouslySetInnerHTML={{
                  __html: addressSingleLine(
                    countryId,
                    site.address,
                    countryList
                  ),
                }}
              />
            </InfoLine>
          )}
          {hasPaymentDelegation && (
            <WarningText>
              {t("CLIENT_INVOICING_SITE_PAYMENT_DELEGATION_NOTICE")}
            </WarningText>
          )}
        </BlockBody>
      </Block>
    );
  }

  return (
    <Block>
      <BlockHeader>{t("CLIENT_INVOICING_SELECTED_SITE")}</BlockHeader>
      <BlockBody>
        <Autocomplete
          id="invoice-site-search"
          fullWidth
          loading={loading}
          options={options}
          getOptionLabel={(option) => `#${option.id} - ${option.name}`}
          filterOptions={(opts, params) => createFilterOptions()(opts, params)}
          onOpen={handleFocus}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") setSearch(newInputValue || "");
          }}
          onChange={(event, newValue) => {
            if (newValue) onSelectSite(newValue);
          }}
          value={null}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("SEARCH")}
              size="small"
              style={autoSearchInputStyles}
              InputProps={{
                ...params.InputProps,
                style: { height: "40px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Image src={searchIcon} alt="search" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <PlaceholderText>{t("CLIENT_INVOICING_NO_SITE")}</PlaceholderText>
      </BlockBody>
    </Block>
  );
};

SelectedSite.propTypes = {
  t: PropTypes.func.isRequired,
  site: PropTypes.object,
  countryId: PropTypes.number,
  countryList: PropTypes.array,
  companyId: PropTypes.number,
  companySites: PropTypes.array,
  onSelectSite: PropTypes.func.isRequired,
  onRemoveSite: PropTypes.func.isRequired,
};

SelectedSite.defaultProps = {
  site: null,
  countryId: null,
  countryList: [],
  companyId: null,
  companySites: [],
};

export default withTranslation()(SelectedSite);
